<template>
  <ListTable
    v-if="!isMobile"
    :columns="columns"
    :data="rows"
    @sortItemSelected="sortItemSelected"
  />
  <div v-else class="orderHistory-flex">
    <div class="btns">
      <div class="input-box">
        <input
          class="checkbox"
          type="checkbox"
          id="hideSymbol"
          v-model="hideOtherSymbol"
        />
        <label for="hideSymbol"></label>
        <span>{{ $t("trading.table.hideSymbol") }}</span>
      </div>
      <button class="state" @click="showDropdown = true">
        {{ $t("trading.table.status") }} {{ showDropdown ? "▲" : "▼" }}
      </button>
      <div class="dropdown" v-if="showDropdown">
        <ul>
          <li
            v-for="item in dropdownItems || []"
            :key="item.key"
            @click.stop="sortItemSelected(item)"
          >
            {{ item.value }}
          </li>
        </ul>
      </div>
    </div>
    <div class="gray-line" v-if="!isMobile"></div>
    <NodataView v-if="rows.length === 0" min-height="200px" />
    <div class="row-wrapper" v-for="row in rows" :key="row.ordid">
      <div class="symbol">
        <span>{{ row.datetime }}</span>
        <p>{{ row.symbol }}USDT</p>
      </div>
      <!-- <div class="sub">
        <span :class="row.side === 'buy' ? 'txt-plus' : 'txt-minus'"
          >{{ typeFormatter(row.type) }} /
          {{ t(`trading.table.${row.side}`) }}</span
        >
      </div> -->

      <div class="coin-info">
        <p>{{ $t("trading.table.type") }}</p>
        <span>{{ typeFormatter(row.type) }}</span>
      </div>

      <div class="coin-info">
        <p>{{ $t("trading.table.direction") }}</p>
        <span :class="row.side === 'buy' ? 'txt-plus' : 'txt-minus'">{{
          t(`trading.table.${row.side}`)
        }}</span>
      </div>

      <div class="coin-info">
        <p>{{ $t("trading.table.average") }}</p>
        <span>{{ formatNumberWithComma(row.average) }}</span>
      </div>
      <div class="coin-info">
        <p>{{ $t("trading.table.executed") }}</p>
        <span>{{ formatNumberWithComma(row.executed) }}</span>
      </div>
      <div class="coin-info">
        <p>{{ $t("trading.table.amount") }}</p>
        <span>{{ formatNumberWithComma(row.amount) }}</span>
      </div>
      <div class="coin-info">
        <p>{{ $t("trading.table.tc") }}</p>
        <span>{{ triggerFomatter(row) }}</span>
      </div>
      <div class="coin-info">
        <p>{{ $t(`trading.table.status`) }}</p>
        <span v-html="statusFormatter(row)"></span>
      </div>
      <!-- <div class="gray-line"></div> -->
      <div class="gray-line" v-if="!isMobile"></div>
    </div>
  </div>
  <ModalConfirm
    :isVisible="confirmDialog.isVisible"
    :content="confirmDialog.content"
    @closeDialogHandler="closeDialogHandler"
  />
  <ModalAlert
    :isVisible="alertDialog.isVisible"
    :content="alertDialog.content"
    @closeDialogHandler="alertDialog.isVisible = false"
    :is-btn="true"
  />
</template>

<script setup>
import ModalConfirm from "@/components/common/modal/ModalConfirm.vue";
import ModalAlert from "@/components/common/modal/ModalAlert.vue";
import ListTable from "@/components/common/list/List.vue";
import NodataView from "@/components/common/noData/NodataView.vue";
import dayjs from "dayjs";
import {
  averageFormatter,
  executedFormatter,
  sideFormatter,
  statusFormatter,
  symbolFormatter,
  triggerFomatter,
  typeFormatter,
} from "@/utils/formatter";
import { amountFormatter, formatNumberWithComma } from "@/utils/order";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const store = useStore();
const dropdownItems = [
  { key: "0", value: t("trading.table.all") },
  { key: "1", value: t("trading.table.filled") },
  { key: "3", value: t("trading.table.cancel") },
  { key: "4", value: t("trading.table.expired") },
];
const confirmDialog = ref({
  isVisible: false,
  key: "",
  content: "",
});
const alertDialog = ref({
  isVisible: false,
  key: "",
  content: "",
});
const showDropdown = ref(false);
const columns = ref([
  { field: "datetime", title: t("trading.table.time"), sortable: false },
  {
    field: "symbol",
    title: t("trading.table.symbol"),
    sortable: false,
    formatter: symbolFormatter,
  },
  {
    field: "type",
    title: t("trading.table.type"),
    sortable: false,
    formatter: typeFormatter,
  },
  {
    field: "side",
    title: t("trading.table.side"),
    sortable: false,
    formatter: sideFormatter,
  },
  { field: "average", title: t("trading.table.average"), sortable: false },
  { field: "executed", title: t("trading.table.executed"), sortable: false },
  { field: "amount", title: t("trading.table.amount"), sortable: false },
  {
    field: "trigger",
    title: t("trading.table.tc"),
    sortable: false,
    formatter: triggerFomatter,
  },
  {
    field: "status",
    title: t("trading.table.status"),
    sortable: true,
    sortItems: dropdownItems,
    formatter: statusFormatter,
  },
]);

const sizeType = computed(() => store.state.transact.sizeType);
const userNo = computed(() => store.state.account.userNo);
const isMobile = computed(() => store.state.account.isMobile);
const reCode = computed(() => store.state.user_history.reCode);
const hideOtherSymbol = computed({
  get: () => store.state.user_history.hideOtherSymbol,
  set: (value) => store.commit("user_history/setHideOtherSymbol", value),
});
const sortStatus = ref("0");
const orderHistoryList = computed(() => {
  const { hideOtherSymbol, orderHistoryList: list } = store.state.user_history;
  const symbol = store.state.transact.ticker;
  const status = sortStatus.value;
  const date = store.state.user_history.date;
  const filteredBySymbol = hideOtherSymbol
    ? list.filter((v) => v.symbol === symbol)
    : list;

  const filteredByStatus =
    status === "0"
      ? filteredBySymbol
      : filteredBySymbol.filter((v) => v.status === status);

  if (isMobile.value) return filteredByStatus;

  const start = dayjs(date.start);
  const end = dayjs(date.end).endOf("day");

  return filteredByStatus.filter((v) => {
    const dt = dayjs(v.datetime, "YYYY-MM-DD HH:mm:ss");
    return (
      (dt.isAfter(start) && dt.isBefore(end)) ||
      dt.isSame(start) ||
      dt.isSame(end)
    );
  });
});
const rows = computed(() => {
  return orderHistoryList.value.map((v) => {
    return {
      ...v,
      average: averageFormatter(v.price, v.type),
      executed: executedFormatter(
        sizeType.value === "USDT" ? v.margin : v.amount,
        v.symbol,
        v.type,
      ),
      amount: amountFormatter(
        sizeType.value === "USDT" ? v.margin : v.amount,
        v.symbol,
      ),
      trigger: v.price,
    };
  });
});

const sortItemSelected = (item) => {
  sortStatus.value = item.key;
  if (showDropdown.value) showDropdown.value = false;
};

const closeDialogHandler = async (payload) => {
  confirmDialog.value.isVisible = false;
  if (payload === 1) {
    await store.dispatch("user_history/postCancelOrderAll", {
      userNo: userNo.value,
      ordid: confirmDialog.value.key,
    });
    await store.dispatch("user_history/getorderHistoryList", userNo.value);
    if (reCode.value !== 0) {
      alertDialog.value.isVisible = true;
      alertDialog.value.content = t(`trading.error.${reCode.value}`);
    }
  }
};
const handleClickOutside = (e) => {
  const isPopup = e.target.closest(".dropdown");
  const isInput = e.target.closest(".state");
  if (!isPopup && !isInput) {
    showDropdown.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>
<style lang="scss" scoped>
@use "@/assets/scss/components/trading/history/openOrders.scss" as openOrders;
@use "@/assets/scss/components/trading/history/orderHistory.scss" as
  orderHistory;
</style>
